import React from 'react';
import { Mail, Linkedin } from 'lucide-react';
import derrickPortrait from '@/assets/partner-derrick-ouma.jpg';
import SEO from '../../components/SEO';

const DerrickOuma = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Derrick Ouma — Law and Code Africa"
        description="Derrick Ouma at Law and Code Africa. LLB Hons, Trainee Advocate. Focused on conveyancing and litigation."
        path="/partners/derrick-ouma"
      />

      {/* Hero Section */}
      <section className="relative py-16 overflow-hidden bg-primary">
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Derrick Ouma</h1>
            <p className="text-xl md:text-2xl text-blue-100 max-w-4xl mx-auto mb-8">
              LLB Hons | Trainee Advocate
            </p>
            <div className="max-w-6xl mx-auto text-lg text-blue-100 space-y-4 text-justify px-4">
              <p>
                Derrick Ouma is a diligent and client-oriented legal mind with primary areas of interest in Conveyancing and Litigation. An Advocates Training Programme (ATP) student at the Kenya School of Law, he combines a methodical approach to land transactions and property due diligence with a strong appetite for courtroom advocacy.
              </p>
              <p>
                He has demonstrated a keen ability to interpret transactional documents, navigate land registry processes, and support contentious matters through legal research, drafting, and engagement with stakeholders across the dispute-resolution chain.
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-12 mb-16">
          <div className="lg:col-span-1">
            <div className="flex flex-col items-center">
              <div className="relative overflow-hidden rounded-lg shadow-lg w-full max-w-sm mb-6 bg-gray-50">
                <img
                  src={derrickPortrait}
                  alt="Derrick Ouma - Partner, Law and Code Africa"
                  loading="lazy"
                  className="w-full h-auto object-cover transition-all duration-700 hover:scale-110 hover:brightness-110"
                />
              </div>
              <div className="bg-gray-50 p-5 md:p-6 rounded-lg w-full max-w-sm">
                <h3 className="text-lg md:text-xl font-bold text-primary mb-4 text-center">Contact Information</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <Mail className="text-secondary flex-shrink-0" size={20} />
                    <span className="text-gray-700 text-sm md:text-base break-all">lawandcodeafrica@gmail.com</span>
                  </div>
                  <div className="flex items-center gap-3 min-w-0">
                    <Linkedin className="text-secondary flex-shrink-0" size={20} />
                    <a
                      href="https://www.linkedin.com/in/derrick-ouma"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline text-sm md:text-base break-all"
                    >
                      Connect on LinkedIn
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2">
            <h2 className="text-3xl font-bold text-primary mb-8">Background and Qualifications</h2>
            <div className="space-y-6 text-gray-700">
              <div className="bg-white border-l-4 border-secondary p-6 rounded-r-lg shadow-sm">
                <h3 className="text-xl font-semibold text-primary mb-3">Educational Foundation</h3>
                <div className="space-y-3">
                  <p>
                    <strong>Kisii University, School of Law (LLB Hons):</strong> Attended Kisii University where he developed a strong grounding in property law, civil procedure, the law of contract, and conveyancing practice. He actively participated in moot court and legal aid clinics during his tenure.
                  </p>
                  <p>
                    <strong>Kenya School of Law:</strong> Currently an Advocates Training Programme (ATP) student.
                  </p>
                </div>
              </div>

              <div className="bg-white border-l-4 border-secondary p-6 rounded-r-lg shadow-sm">
                <h3 className="text-xl font-semibold text-primary mb-3">Professional Qualifications</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-secondary mb-2">Conveyancing Practitioner</h4>
                    <p className="text-sm text-gray-600">Skilled in land searches, transfers, charges, and property due diligence</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-secondary mb-2">Litigation Support</h4>
                    <p className="text-sm text-gray-600">Drafting of pleadings, case management, and observation of court proceedings</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-secondary mb-2">Legal Researcher</h4>
                    <p className="text-sm text-gray-600">Methodical research on statutes, case law, and Land Registry practice</p>
                  </div>
                </div>
              </div>

              <div className="bg-white border-l-4 border-secondary p-6 rounded-r-lg shadow-sm">
                <h3 className="text-xl font-semibold text-primary mb-3">Professional Experience &amp; Community Involvement</h3>
                <div className="space-y-3">
                  <p>
                    Derrick has supported conveyancing and litigation files through pupillage placements, contributing to land transactions, sale agreements, and the drafting of court documents. He continues to engage in legal aid initiatives and student-led legal forums.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Areas of Specialization */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-primary mb-8 text-center">
            Key Practice Areas and Interests
          </h2>
          <p className="text-lg text-gray-600 text-center mb-10 max-w-4xl mx-auto">
            Derrick Ouma's professional interests and developing specializations encompass:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-secondary">
              <h3 className="text-xl font-semibold text-primary mb-3">Conveyancing</h3>
              <p className="text-gray-600">
                Land transactions, sale agreements, transfers, charges, leases, and property due diligence under the Land Act and Land Registration Act.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-secondary">
              <h3 className="text-xl font-semibold text-primary mb-3">Litigation</h3>
              <p className="text-gray-600">
                Civil and commercial litigation support, drafting of pleadings, case management, and engagement with court processes and filings.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DerrickOuma;
